import { useCallback, useMemo, useState } from "react";
import { useImages } from "~/hooks/useImages.ts";
import { useSpace } from "~/providers/SpaceContext.ts";
import { useTemplate } from "~/providers/TemplateContext.ts";
import { runBusyOperation } from "~/services/busyOperation.ts";
import { moveListToSpace } from "~/services/spaceDatabase.ts";
import type { Image } from "~/types/Image.ts";
import type { Space } from "~/types/Space.ts";
import { hasDuplicateEntityName } from "../shared/entityValidation.ts";
import { FadeScrollView } from "../shared/FadeScrollView.tsx";
import { ImageViewerModal } from "../shared/ImageViewerModal.tsx";
import { orderEntityLayouts } from "../shared/orderEntityLayouts.ts";
import { useEmptyEntityBulkEdit } from "../shared/useEmptyEntityBulkEdit.ts";
import { useEntityImageActions } from "../shared/useEntityImageActions.ts";
import { useRevisitOrderedColors } from "../shared/useRevisitOrderedColors.ts";
import { HomeHeader } from "./HomeHeader.tsx";
import type { RowLayout } from "./itemRowProps.ts";
import { ListCard, ListCardPreview } from "./ListCard.tsx";
import { ListHeader } from "./ListHeader.tsx";
import { ListRow } from "./ListRow.tsx";
import { buildListColors } from "./listColors.ts";
import { listCopy } from "./listCopy.ts";
import { computeDropIndex, useListOrdering } from "./listOrdering.ts";
import { ListActions, useListActions } from "./listSectionState.ts";
import { PanelShell } from "./PanelShell.tsx";
import { showActionSheet } from "./showActionSheet.ts";
import { HOME_COPY } from "./styles.ts";
import { TextPromptDialog } from "./TextPromptDialog.tsx";
import { homeColors, homeSpacing } from "./theme.ts";
import { PackingListSummary, SelectionState } from "./types.ts";
import { DragOffset } from "./useDraggableRow.tsx";
import { DragSnapshot, useDragState } from "./useDragState.ts";
import "./listOrdering.css";

type ListSectionProps = {
  lists: PackingListSummary[];
  selection: SelectionState;
  email: string;
  onProfile: () => void;
  onListSelect: (id: string) => void;
};
export const ListSection = (props: ListSectionProps) => {
  const { templateList } = useTemplate();
  const { profile, spaces, spaceId, writeDb } = useSpace();
  const { images } = useImages(spaceId);
  const imageDb = {
    add: writeDb.addImage,
    update: writeDb.updateImage,
    delete: writeDb.deleteImage,
  };
  const imgActions = useEntityImageActions("packingLists", imageDb);
  const listImages = useMemo(() => images.filter((img) => img.type === "packingLists"), [images]);
  const handleMoveToSpace = useCallback(
    (listId: string, targetSpaceId: string) =>
      runBusyOperation(
        async () => {
          await moveListToSpace(spaceId, targetSpaceId, listId);
        },
        { message: "movingList" }
      ),
    [spaceId]
  );
  const actions = useListActions(props.lists, props.selection, templateList, listImages, props.onListSelect);
  const bulkEdit = useEmptyEntityBulkEdit({
    entities: props.lists,
    isEligible: (list) => list.itemCount === 0,
    hasItem: (list, item) => item.packingList === list.id,
    onDelete: (list) => actions.onDelete(list, true),
    copy: listCopy,
    menuKey: "list.bulkRemoveEmptyCount",
    confirmKey: "list.bulkConfirm",
    message: "removingEmptyLists",
  });
  const creation = useCreateListDialog(actions.onAdd, props.lists, !!templateList);
  const drag = useDragState();
  const ordering = useListOrdering(props.lists);
  const [showArchived, setShowArchived] = useState(false);
  const hasArchived = props.lists.some((list) => list.archived);
  const filteredLists = showArchived ? ordering.lists : ordering.lists.filter((list) => !list.archived);
  const colors = useRevisitOrderedColors(filteredLists, buildListColors);
  return (
    <PanelShell>
      <HomeHeader
        title={HOME_COPY.listHeader}
        email={props.email}
        profileImageUrl={profile?.imageUrl}
        onProfile={props.onProfile}
        useSpaceAsTitle
      />
      <ListHeader
        onAdd={creation.open}
        onBulkEdit={bulkEdit.open}
        bulkEditing={bulkEdit.busy}
        showArchived={showArchived}
        hasArchived={hasArchived}
        onToggleArchived={() => setShowArchived((v) => !v)}
      />
      <ListScroll
        lists={filteredLists}
        allLists={props.lists}
        selectedId={props.selection.selectedId}
        actions={actions}
        colors={colors}
        spaces={spaces}
        currentSpaceId={spaceId}
        onMoveToSpace={handleMoveToSpace}
        images={listImages}
        imageLoadingId={imgActions.loadingEntityId}
        hideImagePlaceholder={profile?.hideImagePlaceholder ?? false}
        onImagePress={imgActions.handleImagePress}
        drag={drag}
        onDrop={ordering.drop}
        onListSelect={props.onListSelect}
      />
      <TextPromptDialog
        visible={creation.visible}
        title={HOME_COPY.createListPrompt}
        confirmLabel={HOME_COPY.createListConfirm}
        value={creation.value}
        error={creation.error}
        getError={creation.getError}
        onChange={creation.setValue}
        onCancel={creation.close}
        onSubmitText={creation.submitText}
        onSubmit={creation.submit}
      />
      {imgActions.viewerState && (
        <ImageViewerModal
          visible={true}
          imageUrl={imgActions.viewerState.image.url}
          loading={imgActions.modalLoading}
          textValue={imgActions.textValue}
          textSubmitDisabled={!imgActions.textValue.trim()}
          onTextChange={imgActions.setTextValue}
          onTextSubmit={() => void imgActions.submitText()}
          onClose={imgActions.closeViewer}
          onReplace={imgActions.handleReplace}
          onRemove={imgActions.handleRemove}
        />
      )}
    </PanelShell>
  );
};

type ScrollProps = {
  lists: PackingListSummary[];
  allLists: PackingListSummary[];
  selectedId: string;
  actions: ListActions;
  colors: Record<string, string>;
  spaces: Space[];
  currentSpaceId: string;
  onMoveToSpace: (listId: string, targetSpaceId: string) => void;
  images: Image[];
  imageLoadingId: string | null;
  hideImagePlaceholder: boolean;
  onImagePress: (entityId: string, image?: Image) => void;
  drag: ReturnType<typeof useDragState>;
  onDrop: (snapshot: DragSnapshot, layouts: Record<string, RowLayout>) => void;
  onListSelect: (id: string) => void;
};

const ListScroll = ({
  lists,
  allLists,
  selectedId,
  actions,
  colors,
  spaces,
  currentSpaceId,
  onMoveToSpace,
  images,
  imageLoadingId,
  hideImagePlaceholder,
  onImagePress,
  drag,
  onDrop,
  onListSelect,
}: ScrollProps) => {
  const listIds = lists.map((l) => l.id);
  const separatorIndices = useMemo(() => getSeparatorIndices(lists), [lists]);
  const layouts = orderEntityLayouts(listIds, drag.layouts, homeSpacing.xs, separatorIndices);
  const dropIndex = computeDropIndex(listIds, drag.snapshot, layouts);
  const originalIndex = drag.snapshot ? listIds.indexOf(drag.snapshot.id) : -1;
  const wouldMove = dropIndex !== null && dropIndex !== originalIndex;
  const showBelow = wouldMove && (drag.snapshot?.offsetY ?? 0) > 0;
  const isDropping = drag.snapshot?.frozenY !== undefined;
  return (
    <FadeScrollView style={{ flex: 1 }} drag={drag}>
      <div className="list-ordering" style={{ gap: homeSpacing.xs, paddingTop: homeSpacing.xs }}>
        {lists.map((list, index) => (
          <ListRow key={list.id} id={list.id} separated={separatorIndices.has(index)} onLayout={drag.recordLayout}>
            <ListCard
              list={list}
              lists={allLists}
              isSelected={selectedId === list.id}
              actions={actions}
              color={colors[list.id]}
              spaces={spaces}
              currentSpaceId={currentSpaceId}
              onMoveToSpace={onMoveToSpace}
              image={images.find((img) => img.typeId === list.id)}
              imageLoading={imageLoadingId === list.id}
              hideImagePlaceholder={hideImagePlaceholder}
              showImageMenuAction
              onImagePress={onImagePress}
              hidden={drag.snapshot?.id === list.id}
              onDragStart={() => drag.start(list.id, "")}
              onDragMove={(offset: DragOffset) => drag.move(list.id, offset)}
              onDragEnd={() => drag.end((snapshot) => snapshot && onDrop(snapshot, layouts), layouts)}
              onSelect={onListSelect}
            />
          </ListRow>
        ))}
        {!isDropping && <DropIndicator dropIndex={dropIndex} lists={lists} layouts={layouts} below={showBelow} />}
        <GhostRow lists={lists} colors={colors} drag={drag.snapshot} layouts={layouts} />
      </div>
    </FadeScrollView>
  );
};

const getSeparatorIndices = (lists: PackingListSummary[]): Set<number> => {
  const indices = new Set<number>();
  const lastTemplateIdx = lists.findLastIndex((l) => l.isTemplate && !l.archived);
  const lastPinnedIdx = lists.findLastIndex((l) => l.pinned && !l.isTemplate && !l.archived);
  const lastActiveIdx = lists.findLastIndex((l) => !l.archived);
  if (lastTemplateIdx >= 0 && lastTemplateIdx < lastActiveIdx) indices.add(lastTemplateIdx);
  if (lastPinnedIdx >= 0 && lastPinnedIdx < lastActiveIdx) indices.add(lastPinnedIdx);
  return indices;
};

const useCreateListDialog = (
  create: (name: string, useTemplate: boolean) => Promise<void>,
  lists: PackingListSummary[],
  hasTemplate: boolean
) => {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const open = useCallback(() => {
    setValue("");
    setError(null);
    setVisible(true);
  }, []);
  const close = useCallback(() => setVisible(false), []);
  const getError = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      return trimmed && hasDuplicateEntityName(trimmed, lists) ? HOME_COPY.duplicateListName : null;
    },
    [lists]
  );
  const onChange = useCallback(
    (text: string) => {
      setValue(text);
      setError(getError(text));
    },
    [getError]
  );
  const submitText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const nextError = getError(text);
      if (!trimmed || nextError) {
        setError(nextError);
        return;
      }
      close();
      if (hasTemplate) {
        askUseTemplate(trimmed, create);
      } else {
        void create(trimmed, false);
      }
    },
    [close, create, getError, hasTemplate]
  );
  const submit = useCallback(() => {
    submitText(value);
  }, [submitText, value]);
  return {
    visible,
    value,
    setValue: onChange,
    error,
    getError,
    open,
    close,
    submit,
    submitText,
  } as const;
};

const askUseTemplate = (name: string, create: (name: string, useTemplate: boolean) => Promise<void>) => {
  showActionSheet(HOME_COPY.useTemplateMessage, [
    { text: HOME_COPY.useTemplateNo, onPress: () => void create(name, false) },
    { text: HOME_COPY.useTemplateYes, onPress: () => void create(name, true) },
  ]);
};

type GhostProps = {
  lists: PackingListSummary[];
  colors: Record<string, string>;
  drag: DragSnapshot;
  layouts: Record<string, RowLayout>;
};

const GhostRow = ({ lists, colors, drag, layouts }: GhostProps) => {
  if (!drag) return null;
  const layout = layouts[drag.id];
  if (!layout) return null;
  const list = lists.find((entry) => entry.id === drag.id);
  if (!list) return null;
  const top = drag.frozenY ?? layout.y + drag.offsetY;
  return (
    <div className="list-ordering-ghost" style={{ top, height: layout.height, width: layout.width }}>
      <ListCardPreview list={list} color={colors[list.id]} />
    </div>
  );
};

type DropIndicatorProps = {
  dropIndex: number | null;
  lists: PackingListSummary[];
  layouts: Record<string, RowLayout>;
  below: boolean;
};

const DropIndicator = ({ dropIndex, lists, layouts, below }: DropIndicatorProps) => {
  if (dropIndex === null) return null;
  const targetId = lists[dropIndex]?.id;
  if (!targetId) return null;
  const layout = layouts[targetId];
  if (!layout) return null;
  const top = below ? layout.y + layout.height - 2 : layout.y - 2;
  return <div className="list-ordering-indicator" style={{ top, backgroundColor: homeColors.dropIndicator }} />;
};
